/**
 * Interactive transcript display with word-level highlighting and drag-to-select.
 *
 * Renders the current lesson's words as tappable spans. The active word
 * (synced to playback) gets an underline indicator. Hold-and-drag selects
 * a contiguous range of words wrapped in a blue highlight band.
 *
 * Uses a two-layer word span architecture: every word has an outer slot span
 * (flush, controls band rounding) and an inner pill span (carries status color).
 * Highlighting only changes className — never restructures the DOM.
 *
 * Adapted from the web-feed prototype for UserVideoWord (PWA backend type).
 * Word status is resolved from wordStatusOverrides$ (optimistic local state)
 * with the word's own .status field as fallback.
 */

import { useRef, useMemo, useState, useEffect, useCallback } from "react";
import type React from "react";
import { observer } from "@legendapp/state/react";
import type { UserVideoWord, UserWordStatus } from "../types";
import {
  lesson$,
  activeLessonData$,
  activeWordIndex$,
  selectedRange$,
  selectionFinalized$,
  isDragSelecting$,
  dragHighlight$,
  clearSelection,
  isPaused$,
  scrubbing$,
  selectedWord$,
  seekRequestMs$,
  clearWordTranslation,
  readWordOverride,
  advanceMaxPositionReached,
  popupAnchor$,
} from "../state/videoState";
import { markWordLearning, toggleWordKnown } from "../lib/wordStatus";
import { shouldPauseOnScroll } from "./shouldPauseOnScroll";
import { useTrackFeedEvents } from "../hooks/useTrackFeedEvents";

// ---------------------------------------------------------------------------
// Word status colors
// ---------------------------------------------------------------------------

/** Unknown / new word — amber background to draw attention (iOS Yellow/Lemon #FFD93D). */
const BG_UNKNOWN = "bg-[#FFD93D]";
/** Learning word — softer yellow to show partial progress (iOS LightYellow). */
const BG_LEARNING = "bg-[var(--color-light-yellow)]";
/** Known word — a dark-gray, semi-opaque pill (not pure black) so a learned word
 *  reads as a subtle "got it" chip. Founder feedback: the near-transparent black
 *  was too faint; make it a clearly visible dark gray. White text sits on it. */
const BG_KNOWN = "bg-[rgba(44,44,46,0.7)]";

const HOLD_DELAY_MS = 200;
/** Max gap between two taps on the same word to count them as a double-tap. */
const DOUBLE_TAP_WINDOW_MS = 300;
const MOVE_THRESHOLD_PX = 10;
const AUTO_SCROLL_ZONE_PX = 60;
const AUTO_SCROLL_SPEED = 12;
const MAX_DRAG_WORDS = 12;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * A single word in the transcript using a two-layer architecture:
 * - Outer span: flush slot that controls highlight band rounding
 * - Inner span: pill that carries word status color
 *
 * Highlighting changes only className/style — never DOM structure.
 * Wrapped in `observer()` so it re-renders only when its own word's override
 * status changes, not when other words change.
 */
const WordSpan = observer(function WordSpan({
  word,
  isActive,
  isSelected,
  index,
  outerBg,
  outerRounding,
}: {
  word: UserVideoWord;
  isActive: boolean;
  isSelected: boolean;
  index: number;
  outerBg: string;
  outerRounding: string;
}) {
  // Resolve effective status: local override beats server-provided initial
  // status. Keyed by the canonical wordKey, so EVERY occurrence of this word in
  // the transcript (and in later lessons) reflects the same status live — each
  // WordSpan subscribes to the same observable node and re-renders together.
  const status: UserWordStatus =
    readWordOverride(word.wordKey) ?? word.status;

  let innerColors: string;
  let innerRounding: string;
  if (isSelected) {
    innerColors = `${outerBg} text-white`;
    innerRounding = outerRounding;
  } else if (isActive) {
    // Active word (synced to playback) → keep its normal status colour and add a
    // blue underline below (applied in the chip style). A filled blue highlight
    // band read worse than the underline indicator (founder feedback).
    innerRounding = "rounded";
    innerColors =
      status === "known"
        ? `${BG_KNOWN} text-white`
        : status === "learning"
          ? `${BG_LEARNING} text-[#1C1C1E]`
          : `${BG_UNKNOWN} text-[#1C1C1E]`;
  } else {
    innerRounding = "rounded";
    // Known words get a subtle dark-gray pill (BG_KNOWN) — visible but muted
    // next to the bright amber of new words. Unknown/learning words keep their
    // yellow chip with near-black iOS text (#1C1C1E).
    innerColors =
      status === "known"
        ? `${BG_KNOWN} text-white`
        : status === "learning"
          ? `${BG_LEARNING} text-[#1C1C1E]`
          : `${BG_UNKNOWN} text-[#1C1C1E]`;
  }

  const statusLabel =
    status === "known"
      ? "known"
      : status === "learning"
        ? "learning"
        : "unknown";

  // Row height is content-driven (chip ~26px + the always-reserved 7px underline
  // slot ≈ 33px). That underline reserve already provides ~8px between a chip and
  // the row below, so only a small mb is needed to land on iOS's ~9px ROW_GAP —
  // the old min-h-[44px] + mb-[9px] stacked to ~17px of dark gap between rows.
  return (
    <span
      aria-label={`${word.displayText}, ${statusLabel}`}
      data-word-index={index}
      className="cursor-pointer select-none inline-flex flex-col items-center min-h-[33px] mb-[2px]"
      style={{ lineHeight: "1.2", touchAction: "pan-y" }}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <span
        className={`${isSelected ? outerBg : ""} ${isSelected ? outerRounding : ""} flex items-center px-[3px]`}
      >
        <span
          className={`relative ${innerColors} ${innerRounding}`}
          style={{
            padding: "2px 5px",
            fontSize: 16,
            fontWeight: 700,
          }}
        >
          {word.displayText}
          {/* Active word → blue bar UNDERNEATH the chip (iOS parity: a separate
              underline view below the bubble, not a text-decoration inside it).
              Flush with the chip width (no horizontal overhang). Sits in the
              row's always-reserved ~7px underline slot. */}
          {isActive && (
            <span
              aria-hidden
              className="absolute block bg-[#3FA8F7]"
              style={{
                bottom: -7,
                left: 0,
                right: 0,
                height: 4,
                borderRadius: 2,
              }}
            />
          )}
        </span>
      </span>
    </span>
  );
});

// ---------------------------------------------------------------------------
// Hit-test helper
// ---------------------------------------------------------------------------

/** Find the word index from screen coordinates using elementFromPoint. */
function getWordIndexFromPoint(x: number, y: number): number {
  const el = document.elementFromPoint(x, y);
  if (!el) return -1;
  const wordEl = (el as HTMLElement).closest(
    "[data-word-index]",
  ) as HTMLElement | null;
  if (!wordEl) return -1;
  return parseInt(wordEl.dataset.wordIndex!, 10);
}

// ---------------------------------------------------------------------------
// Transcript component
// ---------------------------------------------------------------------------

interface TranscriptProps {
  /** Words to display. If omitted, reads from activeLessonData$ state. */
  words?: UserVideoWord[];
  /** Video ID for React keys. If omitted, reads from global state. */
  videoId?: string;
  /** Lesson index for React keys. If omitted, reads from global state. */
  lessonIndex?: number;
  /** Whether the transcript is interactive (tap-to-translate). Defaults to true. */
  isInteractive?: boolean;
  /** Lesson ID used for putWordStatus calls. If omitted, inferred from state. */
  lessonId?: number;
  /** True when the transcript floats OVER the video (feed/portrait). Retained
   *  for caller compatibility; known words now stay transparent in all modes
   *  (iOS rebrand parity), so this no longer changes word backgrounds. */
  overlayMode?: boolean;
}

/** Scrollable transcript with auto-scroll and edge fade built into the container. */
const Transcript = observer(function Transcript({
  words: propWords,
  videoId: propVideoId,
  lessonIndex: propLessonIndex,
  isInteractive = true,
  lessonId: propLessonId,
  // NOTE: `overlayMode` is still part of TranscriptProps for caller
  // compatibility (VideoPage/feed thread it), but it is intentionally NOT
  // destructured here: known words are now always transparent (iOS rebrand
  // parity), so overlayMode no longer changes any rendering.
}: TranscriptProps) {
  const { trackWordClicked, trackDoubleTapWord, trackPhraseHighlighted } =
    useTrackFeedEvents();
  // Double-tap detection: a second tap on the SAME word within this window is a
  // double-tap (iOS fires trackDoubleTapWord on a word double-tap, FeedNew.tsx:1353).
  const lastTapRef = useRef<{ index: number; time: number }>({ index: -1, time: 0 });
  // Deferred "tap again to resume" timer (iOS: tapping an already-open paused
  // word resumes playback, but the resume is deferred so a quick second tap
  // registers as a double-tap (mark-known) and cancels it instead).
  const resumeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lessonIndex = propLessonIndex ?? lesson$.currentLessonIndex.get();
  const lessonData = activeLessonData$.get();
  // Stable identity while loading: the `?? []` fallback would otherwise allocate
  // a fresh array each render and churn every hook that depends on `words`.
  const words = useMemo(
    () => propWords ?? lessonData?.userVideoWords ?? [],
    [propWords, lessonData],
  );
  const videoId = propVideoId ?? (lessonData?.youtubeId ?? "");

  const containerRef = useRef<HTMLDivElement>(null);
  const isAutoScrolling = useRef(false);
  const autoScrollRAFId = useRef<number | null>(null);
  const lastProgrammaticScrollTop = useRef<number | null>(null);
  const activeWordIndex = isInteractive ? activeWordIndex$.get() : -1;

  // --- Drag-to-select refs ---
  const isDragging = useRef(false);
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchIdRef = useRef<number | null>(null);
  const anchorRef = useRef(-1);
  const currentRef = useRef(-1);
  const pendingTapIdx = useRef(-1);
  const initialPos = useRef({ x: 0, y: 0 });
  const lastPos = useRef({ x: 0, y: 0 });
  const autoScrollRAF = useRef<number | null>(null);
  const dragMoveHandler = useRef<((e: TouchEvent) => void) | null>(null);

  // --- Read drag observables for rendering ---
  const dragHighlight = isInteractive ? dragHighlight$.get() : null;
  const selectedRange = isInteractive ? selectedRange$.get() : null;

  // Build highlight/selected sets from observables
  const highlightedSet = useMemo(() => {
    const set = new Set<number>();
    if (dragHighlight) {
      const lo = Math.min(dragHighlight[0], dragHighlight[1]);
      const hi = Math.max(dragHighlight[0], dragHighlight[1]);
      for (let i = lo; i <= hi; i++) set.add(i);
    }
    return set;
  }, [dragHighlight]);

  const selectedSet = useMemo(() => {
    const set = new Set<number>();
    if (selectedRange && !dragHighlight) {
      for (let i = selectedRange[0]; i <= selectedRange[1]; i++) set.add(i);
    }
    return set;
  }, [selectedRange, dragHighlight]);

  const hasSelection = highlightedSet.size > 0 || selectedSet.size > 0;

  /** Fire phrase_highlighted for a finalized multi-word selection (iOS parity). */
  const trackPhrase = useCallback(
    (lo: number, hi: number) => {
      if (!words) return;
      const phraseText = words
        .slice(lo, hi + 1)
        .map((w) => w.displayText)
        .join(" ");
      trackPhraseHighlighted(phraseText, hi - lo + 1);
    },
    [words, trackPhraseHighlighted],
  );

  // Anchor the translation bubble above the tapped word / phrase: capture the
  // word tile's viewport rect (the union of the lo/hi tiles for a phrase) so the
  // bubble positions itself over it with a downward pointer (iOS parity).
  const setPopupAnchorFromIndices = useCallback((lo: number, hi: number) => {
    const container = containerRef.current;
    if (!container) return;
    // Union the rects of EVERY selected word (lo..hi), not just the endpoints —
    // when a phrase wraps across lines, an intermediate word can extend further
    // left/right than the first/last, which would mis-center the bubble.
    let left = Infinity;
    let right = -Infinity;
    let top = Infinity;
    for (let i = lo; i <= hi; i++) {
      const el = container.querySelector<HTMLElement>(
        `[data-word-index="${i}"]`,
      );
      if (!el) continue;
      const r = el.getBoundingClientRect();
      left = Math.min(left, r.left);
      right = Math.max(right, r.right);
      top = Math.min(top, r.top);
    }
    if (!Number.isFinite(left)) return; // no word elements found
    // Clamp the anchor to the VISIBLE top of the transcript: when a phrase wraps
    // and earlier words are scrolled out above the 2-line window, their rects sit
    // above the container, which would float the popup way up "where the phrase
    // began" instead of just above the blue selection.
    const ctop = container.getBoundingClientRect().top;
    // Publish the VIDEO column's horizontal bounds (the transcript's positioned
    // ancestor) so the bubble stays within the video, not the whole screen.
    const colEl = container.offsetParent ?? container;
    const col = colEl.getBoundingClientRect();
    popupAnchor$.set({
      cx: (left + right) / 2,
      top: Math.max(top, ctop),
      left: col.left,
      right: col.right,
    });
  }, []);

  /** Tap a word to show its translation panel and mark it as learning. */
  const handleWordTap = useCallback(
    (index: number) => {
      if (!isInteractive || !words) return;
      const word = words[index];
      if (!word) return;
      // Any tap cancels a pending deferred-resume (see tap-again handling below).
      if (resumeTimerRef.current) {
        clearTimeout(resumeTimerRef.current);
        resumeTimerRef.current = null;
      }

      // iOS fires EITHER double_tap_word (same word tapped twice in quick
      // succession) OR word_clicked (otherwise) — a strict if/else, never both
      // (FeedNew.tsx handleWordClicked:1491-1498).
      const now = Date.now();
      const last = lastTapRef.current;
      // Only treat a quick second tap as a KNOWN toggle when this word's panel
      // is actually still open — closing the panel clears selectedWord$ but
      // leaves lastTapRef set, and iOS always re-runs Layer 1 (reopen popup)
      // before toggling. If the panel was closed, fall through to the normal
      // open path instead of toggling against a dismissed popup.
      const panelOpenForWord =
        selectedWord$.peek()?.videoWordId === word.videoWordId;
      if (
        last.index === index &&
        now - last.time <= DOUBLE_TAP_WINDOW_MS &&
        panelOpenForWord
      ) {
        // Double-tap → toggle KNOWN on the already-open word (iOS handleDoubleTap).
        // Leaves the popup open & video paused; the panel checkmark updates from
        // the optimistic wordStatusOverrides$ write. Does NOT re-seek/re-show.
        trackDoubleTapWord();
        lastTapRef.current = { index: -1, time: 0 }; // consume; no triple-tap chains
        // toggleWordKnown cancels this word's still-pending first-tap "learning"
        // write, so only the "known" write is persisted (no two-write race).
        toggleWordKnown(word, propLessonId ?? 0);
        return;
      }

      // Tap-again on the already-open paused word → resume playback ("keep
      // watching", iOS deferred resume). Deferred by the double-tap window so a
      // quick second tap is treated as a double-tap (mark-known) instead.
      const sameWordOpen =
        selectedWord$.peek()?.videoWordId === word.videoWordId &&
        isPaused$.peek();
      if (sameWordOpen) {
        trackWordClicked();
        lastTapRef.current = { index, time: now };
        resumeTimerRef.current = setTimeout(() => {
          resumeTimerRef.current = null;
          clearWordTranslation();
          clearSelection();
          isPaused$.set(false);
        }, DOUBLE_TAP_WINDOW_MS + 50);
        return;
      }

      trackWordClicked();
      lastTapRef.current = { index, time: now };
      clearSelection();
      clearWordTranslation();
      activeWordIndex$.set(index);
      seekRequestMs$.set(word.start);
      isPaused$.set(true);
      selectedWord$.set(word);
      setPopupAnchorFromIndices(index, index);

      // Tapping a word seeks the video to its timestamp, so it counts as
      // reaching that clip-relative position for the 55%/90% activity triggers
      // — this is what lets jumping ahead to a late word generate activities
      // without linear playback (iOS handleWordStatusChanged word-seek branch).
      // word.start and videoStart are absolute full-video ms; the diff is the
      // clip-relative position. Guard videoStart > 0 to match iOS.
      const videoStart = lessonData?.start ?? 0;
      if (word.start && videoStart > 0 && propLessonId !== undefined) {
        advanceMaxPositionReached(propLessonId, word.start - videoStart);
      }

      // Mark as learning (no-op if already known/learning). The shared helper
      // optimistically updates the UI + priority tracker and defers the network
      // persist so a follow-up double-tap (→ "known") cancels it — avoiding two
      // status writes racing on the server.
      markWordLearning(word, propLessonId ?? 0);
    },
    [isInteractive, words, propLessonId, trackWordClicked, trackDoubleTapWord, lessonData, setPopupAnchorFromIndices],
  );

  // Clear any pending deferred-resume timer on unmount OR when this reel stops
  // being interactive (feed swipe to another lesson). Otherwise the timer could
  // fire on the now-inactive transcript and unpause / dismiss the panel of the
  // newly active lesson via the shared isPaused$/selection state.
  useEffect(() => {
    if (!isInteractive && resumeTimerRef.current) {
      clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = null;
    }
    return () => {
      if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    };
  }, [isInteractive]);

  // --- Row position detection for band rounding ---
  const innerContainerRef = useRef<HTMLDivElement>(null);
  const rowPositionsRef = useRef({
    firstOnRow: new Set<number>(),
    lastOnRow: new Set<number>(),
  });
  const [, setRowTick] = useState(0);

  const measureRows = useCallback(() => {
    const container = innerContainerRef.current;
    if (!container) return;
    const wordEls =
      container.querySelectorAll<HTMLElement>("[data-word-index]");
    if (wordEls.length === 0) return;

    const firsts = new Set<number>();
    const lasts = new Set<number>();
    let prevTop = -1;

    for (let j = 0; j < wordEls.length; j++) {
      const el = wordEls[j];
      const idx = parseInt(el.dataset.wordIndex!, 10);
      const top = el.offsetTop;

      if (top !== prevTop) {
        firsts.add(idx);
        if (j > 0)
          lasts.add(parseInt(wordEls[j - 1].dataset.wordIndex!, 10));
      }
      prevTop = top;
    }
    lasts.add(parseInt(wordEls[wordEls.length - 1].dataset.wordIndex!, 10));

    // Only update if changed to avoid infinite loops
    const prev = rowPositionsRef.current;
    if (
      firsts.size !== prev.firstOnRow.size ||
      lasts.size !== prev.lastOnRow.size ||
      ![...firsts].every((v) => prev.firstOnRow.has(v))
    ) {
      rowPositionsRef.current = { firstOnRow: firsts, lastOnRow: lasts };
      setRowTick((t) => t + 1);
    }
  }, []);

  // Measure on mount + resize
  useEffect(() => {
    measureRows();
    const container = innerContainerRef.current;
    if (!container) return;
    const ro = new ResizeObserver(() => measureRows());
    ro.observe(container);
    return () => ro.disconnect();
  }, [measureRows, words]);

  // ---- Auto-scroll during drag ----
  const startAutoScroll = useCallback(() => {
    if (autoScrollRAF.current !== null) return;
    const container = containerRef.current;
    if (!container) return;

    function tick() {
      if (!isDragging.current) {
        autoScrollRAF.current = null;
        return;
      }
      const rect = container!.getBoundingClientRect();
      const containerH = rect.height;

      // Don't auto-scroll if only one word is selected (no drag yet)
      const anchor = anchorRef.current;
      const current = currentRef.current;
      if (anchor === current) {
        autoScrollRAF.current = requestAnimationFrame(tick);
        return;
      }
      // Use the last selected word's position (not the cursor) to drive auto-scroll
      const currentWordEl = container!.querySelector(
        `[data-word-index="${current}"]`,
      );
      if (!currentWordEl) {
        autoScrollRAF.current = requestAnimationFrame(tick);
        return;
      }
      const wordRect = currentWordEl.getBoundingClientRect();
      const wordCenterY = wordRect.top + wordRect.height / 2 - rect.top;

      const atMinLimit = current <= anchor - MAX_DRAG_WORDS + 1;
      const atMaxLimit = current >= anchor + MAX_DRAG_WORDS - 1;

      let delta = 0;
      if (wordCenterY < AUTO_SCROLL_ZONE_PX && !atMinLimit) {
        delta = -AUTO_SCROLL_SPEED * (1 - wordCenterY / AUTO_SCROLL_ZONE_PX);
      } else if (
        wordCenterY > containerH - AUTO_SCROLL_ZONE_PX &&
        !atMaxLimit
      ) {
        delta =
          AUTO_SCROLL_SPEED *
          (1 - (containerH - wordCenterY) / AUTO_SCROLL_ZONE_PX);
      }

      if (delta !== 0) {
        isAutoScrolling.current = true;
        container!.scrollTop += delta;
        // Re-check word under finger after scroll
        let idx = getWordIndexFromPoint(lastPos.current.x, lastPos.current.y);
        if (idx >= 0) {
          idx = Math.max(
            anchor - MAX_DRAG_WORDS + 1,
            Math.min(anchor + MAX_DRAG_WORDS - 1, idx),
          );
          if (idx !== currentRef.current) {
            currentRef.current = idx;
            dragHighlight$.set([anchor, idx]);
          }
        }
      }
      autoScrollRAF.current = requestAnimationFrame(tick);
    }
    autoScrollRAF.current = requestAnimationFrame(tick);
  }, []);

  const stopAutoScroll = useCallback(() => {
    if (autoScrollRAF.current !== null) {
      cancelAnimationFrame(autoScrollRAF.current);
      autoScrollRAF.current = null;
    }
  }, []);

  // ---- Touch event handling for drag-to-select ----
  useEffect(() => {
    if (!isInteractive) return;
    const container = containerRef.current!;
    if (!container) return;

    // --- Layer 1: touchstart on container (passive) ---
    function handleTouchStart(e: TouchEvent) {
      if (touchIdRef.current !== null) return;
      const touch = e.touches[0];
      const idx = getWordIndexFromPoint(touch.clientX, touch.clientY);

      touchIdRef.current = touch.identifier;
      initialPos.current = { x: touch.clientX, y: touch.clientY };
      lastPos.current = { x: touch.clientX, y: touch.clientY };
      pendingTapIdx.current = idx;

      if (idx >= 0) {
        holdTimer.current = setTimeout(() => {
          pendingTapIdx.current = -1; // no longer a tap
          isDragging.current = true;

          anchorRef.current = idx;
          currentRef.current = idx;

          // Kill scroll on this container mid-touch on iOS Safari
          container.style.overflow = "hidden";

          // Pause and seek to the anchor word
          isPaused$.set(true);
          if (words?.[idx]) {
            activeWordIndex$.set(idx);
            seekRequestMs$.set(words[idx].start);
          }

          isDragSelecting$.set(true);
          dragHighlight$.set([idx, idx]);

          // --- Attach non-passive touchmove NOW ---
          function onDragMove(ev: TouchEvent) {
            let t: Touch | null = null;
            for (let i = 0; i < ev.changedTouches.length; i++) {
              if (ev.changedTouches[i].identifier === touchIdRef.current) {
                t = ev.changedTouches[i];
                break;
              }
            }
            if (!t) return;
            if (ev.cancelable) ev.preventDefault(); // kill scroll during drag
            lastPos.current = { x: t.clientX, y: t.clientY };
            let wi = getWordIndexFromPoint(t.clientX, t.clientY);
            if (wi >= 0) {
              const anchor = anchorRef.current;
              wi = Math.max(
                anchor - MAX_DRAG_WORDS + 1,
                Math.min(anchor + MAX_DRAG_WORDS - 1, wi),
              );
              if (wi !== currentRef.current) {
                currentRef.current = wi;
                dragHighlight$.set([anchor, wi]);
              }
            }
          }
          dragMoveHandler.current = onDragMove;
          document.addEventListener("touchmove", onDragMove, {
            passive: false,
          });

          startAutoScroll();
        }, HOLD_DELAY_MS);
      }
    }

    // --- Layer 2: pre-hold touchmove on container (passive) ---
    function handlePreHoldMove(e: TouchEvent) {
      if (isDragging.current || holdTimer.current === null) return;
      let touch: Touch | null = null;
      for (let i = 0; i < e.changedTouches.length; i++) {
        if (e.changedTouches[i].identifier === touchIdRef.current) {
          touch = e.changedTouches[i];
          break;
        }
      }
      if (!touch) return;
      const dx = Math.abs(touch.clientX - initialPos.current.x);
      const dy = Math.abs(touch.clientY - initialPos.current.y);
      if (dx > MOVE_THRESHOLD_PX || dy > MOVE_THRESHOLD_PX) {
        clearTimeout(holdTimer.current!);
        holdTimer.current = null;
        touchIdRef.current = null;
        pendingTapIdx.current = -1;
      }
    }

    // --- Layer 3: touchend + touchcancel on document (passive) ---
    function handleTouchEnd(e: TouchEvent) {
      if (touchIdRef.current === null) return;
      let found = false;
      for (let i = 0; i < e.changedTouches.length; i++) {
        if (e.changedTouches[i].identifier === touchIdRef.current) {
          found = true;
          break;
        }
      }
      if (!found) return;

      const wasDragging = isDragging.current;
      const tapIdx = pendingTapIdx.current;

      // Clean up timer
      if (holdTimer.current) {
        clearTimeout(holdTimer.current);
        holdTimer.current = null;
      }
      touchIdRef.current = null;
      pendingTapIdx.current = -1;

      if (wasDragging) {
        // --- End drag ---
        isDragging.current = false;
        container.style.overflow = ""; // restore scrolling

        if (dragMoveHandler.current) {
          document.removeEventListener("touchmove", dragMoveHandler.current);
          dragMoveHandler.current = null;
        }
        stopAutoScroll();
        isAutoScrolling.current = false;

        // Commit selection — only finalize if multi-word
        const a = anchorRef.current;
        const c = currentRef.current;
        if (a >= 0 && c >= 0 && a !== c) {
          const lo = Math.min(a, c);
          const hi = Math.max(a, c);
          clearWordTranslation();
          selectedRange$.set([lo, hi]);
          isPaused$.set(true);
          selectionFinalized$.set(true);
          setPopupAnchorFromIndices(lo, hi);
          trackPhrase(lo, hi);
        } else if (a >= 0 && a === c) {
          // Held without dragging — treat as a tap-to-translate
          handleWordTap(a);
        }
        isDragSelecting$.set(false);
        dragHighlight$.set(null);
        anchorRef.current = -1;
        currentRef.current = -1;
      } else if (tapIdx >= 0) {
        // --- Tap ---
        handleWordTap(tapIdx);
      }
    }

    function handleTouchCancel() {
      if (holdTimer.current) {
        clearTimeout(holdTimer.current);
        holdTimer.current = null;
      }
      touchIdRef.current = null;
      pendingTapIdx.current = -1;
      if (isDragging.current) {
        isDragging.current = false;
        container.style.overflow = "";
        if (dragMoveHandler.current) {
          document.removeEventListener("touchmove", dragMoveHandler.current);
          dragMoveHandler.current = null;
        }
        stopAutoScroll();
        isAutoScrolling.current = false;
        isDragSelecting$.set(false);
        dragHighlight$.set(null);
      }
      // Clear any previous finalized selection so it doesn't reappear
      // after the cancelled drag (ghost selection bug)
      clearSelection();
    }

    container.addEventListener("touchstart", handleTouchStart, {
      passive: true,
    });
    container.addEventListener("touchmove", handlePreHoldMove, {
      passive: true,
    });
    document.addEventListener("touchend", handleTouchEnd, { passive: true });
    document.addEventListener("touchcancel", handleTouchCancel, {
      passive: true,
    });

    return () => {
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchmove", handlePreHoldMove);
      document.removeEventListener("touchend", handleTouchEnd);
      document.removeEventListener("touchcancel", handleTouchCancel);
      // Clean up drag state if component unmounts mid-drag
      if (dragMoveHandler.current) {
        document.removeEventListener("touchmove", dragMoveHandler.current);
        dragMoveHandler.current = null;
      }
      if (holdTimer.current) {
        clearTimeout(holdTimer.current);
        holdTimer.current = null;
      }
      if (autoScrollRAF.current) {
        cancelAnimationFrame(autoScrollRAF.current);
        autoScrollRAF.current = null;
      }
    };
  }, [isInteractive, handleWordTap, startAutoScroll, stopAutoScroll, words, trackPhrase, setPopupAnchorFromIndices]);

  // ---- Mouse event handling for drag-to-select (desktop) ----
  useEffect(() => {
    if (!isInteractive) return;
    const container = containerRef.current!;
    if (!container) return;

    let mouseDown = false;
    let mouseDragging = false;
    let mouseAnchorIdx = -1;
    let mouseHoldTimer: ReturnType<typeof setTimeout> | null = null;
    let mouseHoldFired = false;

    function handleMouseDown(e: MouseEvent) {
      if (e.button !== 0) return; // only left click
      const idx = getWordIndexFromPoint(e.clientX, e.clientY);
      if (idx < 0) return;

      mouseDown = true;
      mouseAnchorIdx = idx;
      mouseHoldFired = false;
      lastPos.current = { x: e.clientX, y: e.clientY };

      // Show drag highlight after hold delay, mirroring touch behavior
      mouseHoldTimer = setTimeout(() => {
        mouseHoldTimer = null;
        if (!mouseDown || mouseDragging) return;
        mouseHoldFired = true;
        isDragging.current = true;
        anchorRef.current = idx;
        currentRef.current = idx;

        isPaused$.set(true);
        if (words?.[idx]) {
          activeWordIndex$.set(idx);
          seekRequestMs$.set(words[idx].start);
        }

        isDragSelecting$.set(true);
        dragHighlight$.set([idx, idx]);
        startAutoScroll();
      }, HOLD_DELAY_MS);
    }

    function handleMouseMove(e: MouseEvent) {
      if (!mouseDown) return;

      const wi = getWordIndexFromPoint(e.clientX, e.clientY);

      if (!mouseDragging) {
        // Start dragging once the mouse moves to a different word
        if (wi >= 0 && wi !== mouseAnchorIdx) {
          // Cancel hold timer if it hasn't fired yet
          if (mouseHoldTimer) {
            clearTimeout(mouseHoldTimer);
            mouseHoldTimer = null;
          }
          mouseDragging = true;
          isDragging.current = true;
          anchorRef.current = mouseAnchorIdx;
          currentRef.current = mouseAnchorIdx;

          if (!mouseHoldFired) {
            isPaused$.set(true);
            if (words?.[mouseAnchorIdx]) {
              activeWordIndex$.set(mouseAnchorIdx);
              seekRequestMs$.set(words[mouseAnchorIdx].start);
            }
            isDragSelecting$.set(true);
          }

          const clampedWi = Math.max(
            mouseAnchorIdx - MAX_DRAG_WORDS + 1,
            Math.min(mouseAnchorIdx + MAX_DRAG_WORDS - 1, wi),
          );
          dragHighlight$.set([mouseAnchorIdx, clampedWi]);
          currentRef.current = clampedWi;
          if (!mouseHoldFired) startAutoScroll();
        }
        return;
      }

      // During drag — update highlight
      e.preventDefault(); // prevent text selection during drag
      lastPos.current = { x: e.clientX, y: e.clientY };
      if (wi >= 0) {
        const anchor = anchorRef.current;
        const clamped = Math.max(
          anchor - MAX_DRAG_WORDS + 1,
          Math.min(anchor + MAX_DRAG_WORDS - 1, wi),
        );
        if (clamped !== currentRef.current) {
          currentRef.current = clamped;
          dragHighlight$.set([anchor, clamped]);
        }
      }
    }

    function handleMouseUp() {
      if (!mouseDown) return;

      if (mouseHoldTimer) {
        clearTimeout(mouseHoldTimer);
        mouseHoldTimer = null;
      }

      if (mouseDragging || mouseHoldFired) {
        mouseDragging = false;
        isDragging.current = false;
        stopAutoScroll();
        isAutoScrolling.current = false;

        const a = anchorRef.current;
        const c = currentRef.current;
        if (a >= 0 && c >= 0 && a !== c) {
          const lo = Math.min(a, c);
          const hi = Math.max(a, c);
          clearWordTranslation();
          selectedRange$.set([lo, hi]);
          isPaused$.set(true);
          selectionFinalized$.set(true);
          setPopupAnchorFromIndices(lo, hi);
          trackPhrase(lo, hi);
        } else if (a >= 0 && a === c) {
          // Held without dragging — treat as a tap-to-translate
          handleWordTap(a);
        }
        isDragSelecting$.set(false);
        dragHighlight$.set(null);
        anchorRef.current = -1;
        currentRef.current = -1;
      } else if (mouseAnchorIdx >= 0) {
        // Released without dragging or hold — treat as a tap
        handleWordTap(mouseAnchorIdx);
      }

      mouseDown = false;
      mouseAnchorIdx = -1;
      mouseHoldFired = false;
    }

    container.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      container.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isInteractive, words, handleWordTap, startAutoScroll, stopAutoScroll, trackPhrase, setPopupAnchorFromIndices]);

  // ---- Scroll-position tracking for dynamic mask ----
  const [scrollPos, setScrollPos] = useState({ atTop: true, atBottom: false });

  const updateScrollPos = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const atTop = el.scrollTop < 5;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 5;
    setScrollPos((prev) => {
      if (prev.atTop === atTop && prev.atBottom === atBottom) return prev;
      return { atTop, atBottom };
    });
  }, []);

  // Re-check after content changes (e.g. new lesson)
  useEffect(updateScrollPos, [words, updateScrollPos]);

  // ---- Pause on manual scroll ----
  const isPaused = isInteractive ? isPaused$.get() : false;
  // While the user drags the scrubber the video is effectively paused, but we
  // still want the transcript to auto-scroll to the scrubbed word in real time.
  const scrubbing = isInteractive ? scrubbing$.get() : false;

  const cancelAutoScroll = useCallback(() => {
    if (autoScrollRAFId.current !== null) {
      cancelAnimationFrame(autoScrollRAFId.current);
      autoScrollRAFId.current = null;
    }
    lastProgrammaticScrollTop.current = null;
  }, []);

  // Immediately cancel auto-scroll on user touch/wheel input (fires before scroll event)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onUserInput = () => {
      if (autoScrollRAFId.current !== null && !isDragging.current) {
        // Interrupt the row-following auto-scroll so the transcript doesn't
        // fight the user — but do NOT pause the video. iOS never pauses on a
        // transcript scroll; only a tap (overlay or word) toggles playback.
        cancelAutoScroll();
      }
    };
    el.addEventListener("touchmove", onUserInput, { passive: true });
    el.addEventListener("wheel", onUserInput, { passive: true });
    return () => {
      el.removeEventListener("touchmove", onUserInput);
      el.removeEventListener("wheel", onUserInput);
    };
  }, [cancelAutoScroll, isInteractive]);

  const handleScroll = useCallback(() => {
    updateScrollPos();

    const el = containerRef.current;
    // `shouldPauseOnScroll` discriminates a genuine user scroll from a
    // programmatic auto-scroll / drag-select. On a genuine user scroll we only
    // cancel the auto-follow animation — we do NOT pause the video. Reading the
    // words by scrolling must not stop playback (iOS parity); only a tap pauses.
    const userScrolled = shouldPauseOnScroll(
      isDragging.current,
      isInteractive,
      isPaused$.peek(),
      autoScrollRAFId.current !== null,
      lastProgrammaticScrollTop.current,
      el?.scrollTop ?? 0,
    );

    if (!userScrolled) return;

    cancelAutoScroll();
  }, [updateScrollPos, isInteractive, cancelAutoScroll]);

  // ---- Row-aware auto-scroll (advances every 2 rows) ----
  const lastPairRef = useRef(-1);

  // Reset pair tracking when words change (new lesson) or on unpause
  useEffect(() => {
    lastPairRef.current = -1;
  }, [words]);

  // On unpause, force re-sync to the correct pair
  const prevPausedRef = useRef(isPaused);
  useEffect(() => {
    if (prevPausedRef.current && !isPaused) {
      lastPairRef.current = -1; // force scroll on next activeWordIndex tick
    }
    prevPausedRef.current = isPaused;
  }, [isPaused]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || activeWordIndex < 0 || (isPaused && !scrubbing)) return;

    // Measure visual rows from DOM
    const wordEls = el.querySelectorAll<HTMLElement>("[data-word-index]");
    const elTop = el.getBoundingClientRect().top;
    const rowTops: number[] = [];

    wordEls.forEach((w) => {
      const top = Math.round(
        w.getBoundingClientRect().top - elTop + el.scrollTop,
      );
      if (!rowTops.some((rt) => Math.abs(rt - top) < 8)) {
        rowTops.push(top);
      }
    });
    rowTops.sort((a, b) => a - b);
    if (rowTops.length === 0) return;

    // Find which row the active word is on
    const wordEl = el.querySelector(`[data-word-index="${activeWordIndex}"]`);
    if (!wordEl) return;
    const wordTop = Math.round(
      wordEl.getBoundingClientRect().top - elTop + el.scrollTop,
    );
    const activeRow = rowTops.findIndex((rt) => Math.abs(rt - wordTop) < 8);
    if (activeRow < 0) return;

    // iOS keeps a 2-line focus window and scrolls ONE row at a time as the
    // active word advances (not in 2-row chunks): the active line sits one row
    // down from the top, so the line just spoken fades above it and the next
    // line is the second focus line below, with the upcoming line fading off the
    // bottom. lastPairRef now tracks the active ROW so we only scroll on a row
    // change, not on every word within a line.
    if (activeRow === lastPairRef.current) return;
    lastPairRef.current = activeRow;

    // Cancel any in-flight animation before starting a new one
    cancelAutoScroll();
    const rowPitch =
      rowTops.length > 1 ? rowTops[1] - rowTops[0] : 37;
    const maxScroll = Math.max(0, el.scrollHeight - el.clientHeight);
    const target =
      activeRow === 0
        ? 0
        : Math.max(0, Math.min(rowTops[activeRow] - rowPitch, maxScroll));

    const startTop = el.scrollTop;
    const distance = target - startTop;

    // Skip animation if already at target (prevents rubber banding at top)
    if (Math.abs(distance) < 1) {
      return;
    }

    // Scale duration by distance so short scrolls don't feel sluggish
    const MAX_DURATION = 800;
    const MIN_DURATION = 200;
    const maxExpectedDistance = el.clientHeight;
    const SCROLL_DURATION = Math.max(
      MIN_DURATION,
      MAX_DURATION * Math.min(Math.abs(distance) / maxExpectedDistance, 1),
    );
    const startTime = performance.now();

    function animateScroll(now: number) {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / SCROLL_DURATION, 1);
      // ease-out cubic
      const ease = 1 - Math.pow(1 - t, 3);
      const newTop = startTop + distance * ease;
      el!.scrollTop = newTop;
      lastProgrammaticScrollTop.current = Math.round(newTop);
      if (t < 1) {
        autoScrollRAFId.current = requestAnimationFrame(animateScroll);
      } else {
        autoScrollRAFId.current = null;
      }
    }
    autoScrollRAFId.current = requestAnimationFrame(animateScroll);

    return () => {
      cancelAutoScroll();
    };
  }, [activeWordIndex, words, isPaused, scrubbing, cancelAutoScroll]);

  // ---- Mask gradient: an ALWAYS-ON ~1-row fade at top and bottom (iOS keeps a
  // fixed top/bottom fade vignette), so the focus window reads as two clear lines
  // of dialogue with the line above and below fading off. The fade is softened at
  // the true start/end of the transcript (atTop/atBottom) so the first/last line
  // stays legible rather than fading the active word itself.
  const maskImage = useMemo(() => {
    // Light edge fades only — enough to hint the line above/below without ever
    // washing out the two focus lines (heavy fades made words unreadable).
    const topFade = scrollPos.atTop
      ? "black 0%,"
      : "transparent 0%, rgba(0,0,0,0.6) 6px, black 16px,";
    const bottomFade = scrollPos.atBottom
      ? "black 100%"
      : "black calc(100% - 16px), rgba(0,0,0,0.6) calc(100% - 6px), transparent 100%";
    return `linear-gradient(to bottom, ${topFade} ${bottomFade})`;
  }, [scrollPos]);

  return (
    <div
      role="region"
      aria-label="Transcript"
      ref={containerRef}
      className="h-full overflow-y-auto px-6 py-2"
      onScroll={handleScroll}
      onPointerDown={(e) => e.stopPropagation()}
      style={
        {
          touchAction: "pan-y",
          overscrollBehavior: "contain",
          scrollbarWidth: "none",
          maskImage,
          WebkitMaskImage: maskImage,
        } as React.CSSProperties
      }
    >
      <div ref={innerContainerRef} className="flex flex-wrap">
        {words?.map((word, i) => {
          const inHighlight = highlightedSet.has(i);
          const inSelected = selectedSet.has(i);
          const inBand = inHighlight || inSelected;
          const bandSet = inHighlight
            ? highlightedSet
            : inSelected
              ? selectedSet
              : null;
          const prevInBand = bandSet ? bandSet.has(i - 1) : false;
          const nextInBand = bandSet ? bandSet.has(i + 1) : false;

          const isFirstOnRow = rowPositionsRef.current.firstOnRow.has(i);
          const isLastOnRow = rowPositionsRef.current.lastOnRow.has(i);

          const roundLeft = !inBand || !prevInBand || isFirstOnRow;
          const roundRight = !inBand || !nextInBand || isLastOnRow;

          let outerRounding = "";
          if (roundLeft && roundRight) outerRounding = "rounded";
          else if (roundLeft) outerRounding = "rounded-l";
          else if (roundRight) outerRounding = "rounded-r";

          const outerBg = inBand ? "bg-[#3FA8F7]" : "bg-transparent";
          const isActive = hasSelection ? false : i === activeWordIndex;

          return (
            <WordSpan
              key={`w-${videoId}-${lessonIndex}-${i}`}
              word={word}
              index={i}
              isActive={isActive}
              isSelected={inBand}
              outerBg={outerBg}
              outerRounding={outerRounding}
            />
          );
        })}
      </div>
    </div>
  );
});

export { Transcript };
